import {
  AutocompleteProps,
  Badge,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import AppFilterIcon from '_icons/filterAltOutlined.svg?react'
import { ProjectStateAsString } from 'controllers/Project/ProjectStateMachine'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  onProjectStateChange: (projectState: ProjectStateAsString[]) => void
  selectedProjectStates: ProjectStateAsString[]
  children?: React.ReactElement<any>
  autoCompleteProps?: Partial<
    AutocompleteProps<ProjectStateAsString, true, false, false>
  >
}

import { useAppSelector } from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import { ProjectFilterContext } from '../context/ProjectFilterContext'

const useStyles = makeStyles({ name: 'project-state-selector' })((theme) => ({
  menuItems: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  menuActions: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: theme.spacing(2),
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}))

const options = Object.values(ProjectStateAsString) as ProjectStateAsString[]

const myProjectTitle = {
  buyer: {
    translationKey: 'common:my-projects-buyer',
    defaultText: 'shows only projects create by me',
  },
  seller: {
    translationKey: 'common:my-projects-seller',
    defaultText:
      'shows only project created by me or where I am the sales representative',
  },
}

export const ProjectStateSelector = React.memo(function ProjectStateSelector(
  props: Props,
) {
  const { t } = useTranslation()

  const { classes } = useStyles()
  const [selectedItems, setSelectedItems] = React.useState<
    ProjectStateAsString[]
  >(props.selectedProjectStates)

  useEffect(() => {
    setSelectedItems(props.selectedProjectStates)
  }, [props.selectedProjectStates])

  const timeout = useRef<NodeJS.Timeout>(undefined)

  const handleChange = (_, newValue: ProjectStateAsString[]) => {
    if (timeout.current) {
      clearTimeout(timeout.current)
    }

    setSelectedItems(newValue)

    timeout.current = setTimeout(() => {
      props.onProjectStateChange(newValue)
    }, 2000)
  }

  const buttonRef = useRef<HTMLButtonElement>(null)
  const [menuOpen, setMenuOpen] = React.useState(false)

  const anchorEl = buttonRef.current

  const filterContext = React.useContext(ProjectFilterContext)

  const isBuyer = useAppSelector(
    (state) => state.user.organizationContext.isBuyingParty,
  )

  return (
    <div>
      <Tooltip
        title={t('common:project-state-filter', 'filter by project state')}
      >
        <Badge badgeContent={selectedItems?.length || 0} color="primary">
          <IconButton
            size="small"
            ref={buttonRef}
            onClick={() => setMenuOpen(true)}
          >
            <AppFilterIcon style={{ width: '24px' }} />
          </IconButton>
        </Badge>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
      >
        <MenuItem
          className={classes.menuItems}
          onClick={() => {
            filterContext.setOnlyMyProjects(!filterContext.onlyMyProjects)
          }}
        >
          <Switch size="small" checked={filterContext.onlyMyProjects} />
          <div>
            <Typography>my projects only</Typography>
            <Typography variant="caption" color="textSecondary">
              {isBuyer
                ? t(
                    myProjectTitle.buyer.translationKey,
                    myProjectTitle.buyer.defaultText,
                  )
                : t(
                    myProjectTitle.seller.translationKey,
                    myProjectTitle.seller.defaultText,
                  )}
            </Typography>
          </div>
        </MenuItem>
        {!isBuyer && (
          <MenuItem
            className={classes.menuItems}
            onClick={() => {
              filterContext.setOnlyCreatedInPortal(
                !filterContext.onlyCreatedInPortal,
              )
            }}
          >
            <Switch size="small" checked={filterContext.onlyCreatedInPortal} />
            <div>
              <Typography>self-service projects only</Typography>
              <Typography variant="caption" color="textSecondary">
                filter projects created through the self-service portal
              </Typography>
            </div>
          </MenuItem>
        )}
        {/* <MenuItem className={classes.menuItems}>
          <Switch size="small" checked={filterContext.showCanceledProjects} />
          <Typography>include cancelled and expired projects</Typography>
        </MenuItem> */}
        <div className={classes.menuActions}>
          <Typography
            color="textSecondary"
            variant="caption"
            // fontWeight={600}
          >
            {t('common:filter-project-states', 'filter by project status')}
          </Typography>
          <div>
            <Button
              variant="text"
              onClick={() => {
                handleChange(null, [])
              }}
            >
              {t('common:clear-all')}
            </Button>
            <Button
              variant="text"
              onClick={() => {
                handleChange(null, options)
              }}
            >
              {t('common:select-all')}
            </Button>
          </div>
        </div>
        {options.map((option) => (
          <MenuItem
            key={option}
            onClick={() => {
              if (selectedItems?.includes(option)) {
                handleChange(
                  null,
                  selectedItems.filter((item) => item !== option),
                )
              } else {
                handleChange(null, [...selectedItems, option])
              }
            }}
            className={classes.menuItems}
            selected={selectedItems?.includes(option)}
          >
            <Switch size="small" checked={selectedItems?.includes(option)} />
            <Typography>
              {option === ProjectStateAsString.CANCELLED
                ? t(`common:state-cancelled-expired`, `cancelled/expired`)
                : t(`project:state-${option.toLowerCase()}`)}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </div>
  )
})
